import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { EXPAND_STEP_INTO_PLAN } from '../../../graphql/mutations';
import { GET_PLAN, GET_JOB } from '../../../graphql/queries';
import { colors } from '../../../theme/colors';

interface ExpandStepButtonProps {
  planId: string;
  stepId: string;
  stepTitle: string;
  /** If set, the step already has a subplan - show link instead of expand button */
  expansionPlanId?: string | null;
}

/**
 * Button to expand a step into its own subplan.
 *
 * When clicked, opens a dialog for the user to provide a prompt describing
 * how they want to break down the step. Enqueues a background job and polls
 * for completion, then navigates to the new subplan.
 */
export const ExpandStepButton: React.FC<ExpandStepButtonProps> = ({
  planId,
  stepId,
  stepTitle,
  expansionPlanId,
}) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);

  const [expandStep, { loading: submitting, error }] = useMutation(EXPAND_STEP_INTO_PLAN, {
    onCompleted: (data) => {
      if (data?.expandStepIntoPlan?.jobId) {
        setJobId(data.expandStepIntoPlan.jobId);
      }
    },
  });

  // Poll for job completion
  const { data: jobData } = useQuery(GET_JOB, {
    variables: { id: jobId },
    skip: !jobId,
    pollInterval: 3000,
  });

  const jobStatus = jobData?.job?.status;
  const jobResultRef = jobData?.job?.resultRef;
  const generating = !!jobId && jobStatus !== 'SUCCEEDED' && jobStatus !== 'FAILED';

  // Navigate when job completes
  useEffect(() => {
    if (jobStatus === 'SUCCEEDED' && jobResultRef) {
      setDialogOpen(false);
      setPrompt('');
      setJobId(null);
      navigate(`/app/plans/${jobResultRef}`);
    }
  }, [jobStatus, jobResultRef, navigate]);

  const handleExpand = async () => {
    if (!prompt.trim()) return;

    try {
      await expandStep({
        variables: {
          input: {
            planId,
            stepId,
            prompt: prompt.trim(),
          },
        },
      });
    } catch (err) {
      console.error('Failed to expand step:', err);
    }
  };

  // If step already has an expansion, show link to subplan
  if (expansionPlanId) {
    return (
      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border}` }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={() => navigate(`/app/plans/${expansionPlanId}`)}
          sx={{ textTransform: 'none' }}
        >
          View Subplan
        </Button>
        <Typography variant="caption" sx={{ ml: 1, color: colors.textSecondary }}>
          This step has been expanded into a detailed plan
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border}` }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AccountTreeIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Expand into Subplan
        </Button>
        <Typography variant="caption" sx={{ ml: 1, color: colors.textSecondary }}>
          Break this step into a detailed plan
        </Typography>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && !generating && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Expand Step into Subplan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
            Create a detailed subplan to break down "{stepTitle}" into smaller steps.
            Describe how you'd like this step to be broken down:
          </Typography>

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            placeholder="e.g., Break this into 3-4 actionable steps focusing on..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={submitting || generating}
            sx={{ mt: 1 }}
          />

          {generating && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<CircularProgress size={16} />}>
              Generating subplan... This may take a minute.
            </Alert>
          )}

          {jobStatus === 'FAILED' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {jobData?.job?.errorMessage || 'Subplan generation failed'}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setJobId(null); }} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExpand}
            disabled={submitting || generating || !prompt.trim()}
            startIcon={submitting || generating ? <CircularProgress size={16} color="inherit" /> : <AccountTreeIcon />}
          >
            {generating ? 'Creating...' : submitting ? 'Submitting...' : 'Create Subplan'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpandStepButton;
